/**
 * Every number the mission generator uses (03 §1, §5; 02 §12), in one place. Sim tunables
 * live in `src/sim/tuning.ts`; these only shape maps, so they stay with the generator.
 */

export type Range = readonly [min: number, max: number];

export const MAP = {
  /** Metres per tile: the spec's band and patch sizes are in tiles. */
  tile: 2,
  widthTiles: 48,
  lengthTiles: 160,
  /** Out-of-bounds strip south of the drop band (03 §1). */
  badlands: 8,
  /** Nothing that matters is placed closer than this to the east and west edges. */
  edge: 4,
} as const;

/** Band depths in metres (03 §5.1 gives them in tiles). */
export const BANDS = {
  drop: [56, 72] as Range,
  approach: [32, 48] as Range,
  belt: [24, 36] as Range,
  goal: [48, 64] as Range,
  approachCount: [3, 5] as Range,
} as const;

export const CLOCK = {
  baseSeconds: 600,
  lowThreat: 1.15,
  highThreat: 0.9,
  shortFuse: 0.75,
  /** The shortest valid route may use at most this much of the clock (03 §5.2). */
  feasibleFraction: 0.55,
} as const;

/** 02 §12: 40 points at TR 1 rising to 200 at TR 9, biased north (03 §5.1 step 6). */
export const BUDGET = {
  at1: 40,
  at9: 200,
  split: { drop: 0.1, approach: 0.35, belt: 0.3, goal: 0.25 },
  /** Share of each pool kept for mines, wire and posts, which are laid after the garrison. */
  fieldworks: 0.22,
  fieldworksDense: 0.38,
  relax: 0.9,
  rerolls: 20,
  relaxations: 3,
} as const;

export const COST = {
  sentry: 3, patrol2: 8, patrol3: 12, elite: 2, flamethrower: 8, rocketTrooper: 8, outpost: 9,
  mgNest: 10, mannedTurret: 12, bunker: 16, turretBunker: 18, miniBunker: 14,
  minefield: 6, wireRun: 3, proximityPost: 3, searchlight: 5, alarmPost: 4, barracks: 10,
  /** ROUND8: a handler and his tracker dog - two bodies on a beat, as a patrol of two, one gun and one nose. */
  dogTeam: 8,
} as const;

/**
 * ROUND8: where the generator puts dog teams (`dogTeams.ts`). How many is the roguelike layer's call
 * (`params.dogTeams`); how a dog behaves is the sim's (`DOGS`, src/sim/tuningDogs.ts).
 */
export const DOG_GEN = {
  /** Never more teams than this on one map, whatever is asked: two native and the Dog Patrols card's one. */
  maxTeams: 3,
  /** The beat runs east-west across an approach band, this far in from the edge strip (`MAP.edge`)... */
  beatInset: 6,
  /** ...at the band's middle, give or take this share of its depth, and tilted by up to the same share. */
  beatJitter: 0.12,
  /** Tries at a beat per team before the attempt is given up as a problem (and re-rolled). */
  tries: 6,
} as const;

export const LIMITS = {
  props: 440,
  spawnsMin: 10,
  spawnsAt1: 14,
  spawnsAt9: 40,
  /** Flamethrowers and rocket troopers appear from this threat (02 §9.1). */
  specialsFrom: 5,
  /** Mini-bunkers outside the SAM site (02 §9.2). */
  miniBunkerFrom: 6,
  minefieldInDropFrom: 4,
} as const;

/** Linear obstacles and their crossings (03 §5.1 step 3). */
export const LINES = {
  /** Wire, hedge and pipe; a wall is laid in far shorter pieces so a breach is a gap, not a face (`WALLS.pieceMax`). */
  pieceMax: 12,
  roadGap: 6,
  quietGap: 5,
  extraGap: 4,
  minCrossingSpacing: 12,
  /** x-lanes (as fractions of the width) for the road side and the quiet side. */
  roadLane: [0.64, 0.84] as Range,
  quietLane: [0.13, 0.31] as Range,
  roadDrift: 9,
  quietDrift: 12,
  roadWidth: 4.2,
} as const;

/** The last ravine of the chain, where it crosses the belt (obstacles.ts). */
export const RAVINE = {
  /** It ends this far short of the belt's north edge... */
  beltStandOff: 3,
  /** ...or this far south of the middle of a belt with a water barrier in it: clear of the south bank. */
  riverStandOff: 10,
} as const;

export const COMPOUND = {
  northMargin: [8, 12] as Range,
  southMargin: 6,
  sideMargin: [6, 9] as Range,
  /** N-S sides are columns of pieces at most this long, turned a quarter (props.ts swaps their boxes). */
  columnPiece: 1.1,
  gateWidth: 6,
  sideGateWidth: 5,
  pickupDistance: [9, 18] as Range,
} as const;

export const RIVER = {
  width: [10, 14] as Range,
  weakIce: [6, 10] as Range,
  weakIceRadius: [2, 3.5] as Range,
} as const;

export const MINES = {
  patch: { w: 12, d: 8 },
  density: 6,
  /**
   * Dense Minefields sows each patch this much thicker. Only half of the modifier's "twice the
   * mines": `BUDGET.fieldworksDense` gives the fieldworks a bigger share of the pool as well, so
   * more patches are bought too. Measured end to end, the two come to 1.7x at threat 3 and 3.0x at
   * threat 9 (tests/unit/round6Content.test.ts keeps the card honest about it).
   */
  denseFactor: 1.5,
  /**
   * Below this threat every minefield the budget buys is already marked, so there is nothing for
   * Sapper's Sketch to uncover (`minesMarked`). The briefing does not offer the card there: a
   * 6 RP card that changes nothing is the same defect as a perk that pays nothing (R6).
   */
  hiddenFrom: 6,
  /** Pipeline: a closed ring this thick, so one grenade (4 m clear radius) opens a lane. */
  ringInner: 8,
  ringOuter: 14,
  ringDensity: 8,
} as const;

/** One scatter prop per this many square metres, by band (03 §5.1 step 5). */
export const SCATTER = {
  areaPerProp: { drop: 95, approach: 58, belt: 170, goal: 130 },
  spacing: { drop: 5, approach: 4, belt: 7, goal: 6 },
  clearingsPerBand: 4,
  clearing: 8,
} as const;

/** What the validator measures (03 §5.2). */
export const VALIDATE = {
  dropBandConnected: 0.8,
  podZone: 6,
  podZonesPerBand: 3,
  enemyFreeRadius: 20,
  enemyFreeFraction: 0.6,
  vicinity: 2.5,
  overlapTolerance: 0.05,
  barrierRow: 0.75,
} as const;

/**
 * The quiet-path gate (03 §5.2): the validator's own cheap model of the garrison's eyes. Ranges,
 * fields of view and speeds come from `src/sim/tuning.ts`; these are the numbers the sim keeps
 * in its code (perception.ts, enemies/index.ts) and the knobs of the model itself.
 */
export const QUIET = {
  /** Simulated time in 32-step words. A step is one metre of crawling (1.11 s): 4 words = 142 s >= the spec's 120 s. */
  windowWords: 4,
  /** A patrolling soldier pauses 1-3 s at every node: the model uses the mean. */
  dwellSeconds: 2,
  /** A bored sentry glances this far either side of his post's facing. */
  glance: 0.6,
  /** Eye height of a soldier, and the height of a crawling man, as `perceive` has them. */
  eye: 1.6,
  crawlHeight: 0.4,
  /** A walking man follows grid cells, so his head swings off the straight leg: this, plus an eighth of a turn. */
  headingSlack: 0.12,
  /** Patrol members whose routes run this close together are watched for as one file, from the lead man. */
  fileReach: 5,
  /** The route ends this close to the goal zone's edge: outside the wall, not through it. */
  goalMargin: 2.5,
} as const;

/** Tag Recovery (04 §2.3): the fallen ranger's marker. */
export const TAGS = {
  /** How far it stands off the straight line from the start to the goal: a real detour, not a hike. */
  detour: [25, 60] as Range,
  /** Clear ground around it. */
  clearing: 1.5,
  tries: 400,
} as const;

/**
 * Wind speed in m/s by weather: clear drifts a pod 0-4 m in its 6 s fall, storms 8-20 m (02 §3.2).
 *
 * ROUND5: the three storm weathers share the upper band on purpose. The Storm card says "heavy
 * wind" and pays one multiplier - 1.25 - whatever ground it is flown over, so a temperate storm
 * that blew like a wet Tuesday was the card charging arctic money for desert nothing. It used to:
 * temperate's storm was `rain`, and drew rain's band. A thunderstorm draws this one.
 */
export const WIND = {
  clear: [0, 0.67] as Range,
  snow: [0.67, 1.33] as Range,
  rain: [0.67, 1.33] as Range,
  blizzard: [1.33, 3.33] as Range,
  sandstorm: [1.33, 3.33] as Range,
  thunderstorm: [1.33, 3.33] as Range,
} as const;
